import React, { useEffect } from "react";

import { Col, Container, Form, Row } from "react-bootstrap";

import { isUndefined } from "lodash";
import { useForm } from "react-hook-form";

import { ArtworkInput, ArtworkMedium, ResaleInput } from "@api/graphql";

import { FormErrorText } from "../form/FormErrorText";
import FormNavigationButtons from "../form/FormNavigationButtons";
import FormsHeader from "../form/FormsHeader";
import { FormStepProps } from "../form/MultiStepForm";

const isValidDimension = (value: number) => (!value ? true : String(value).match(/^\d+(\.\d{1,2})?$/) !== null);

interface ArtworkDetailsProps extends FormStepProps<ResaleInput, ArtworkInput> {
  isInsertMode?: boolean;
  submitRef: React.RefObject<HTMLButtonElement>;
  onValidationStatus?: (isValid: boolean) => void;
}

export const ArtworkDetails = ({
  initialFormData,
  onCancel,
  onSubmit,
  isInsertMode = true,
  submitRef,
  onValidationStatus,
}: ArtworkDetailsProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ArtworkInput>({ defaultValues: initialFormData });

  useEffect(() => {
    if (onValidationStatus) {
      onValidationStatus(isValid);
    }
  }, [isValid, onValidationStatus]);

  const handleCancel = () => onCancel && onCancel();

  const setDimension = (value: string) => {
    if (value === "") {
      return null;
    }

    const number = typeof value === "number" ? parseFloat(value) : NaN;
    return isNaN(number) ? value : number;
  };

  return (
    <Form>
      <Container fluid>
        <FormsHeader isInsertMode={isInsertMode} title="Artwork details" />

        <Row className="mb-4">
          <Col>
            <Form.Group controlId="artworkTitle">
              <Form.Label>* Artwork title</Form.Label>
              <Form.Control
                {...register("artwork_name", { required: true })}
                type="text"
                placeholder="Enter artwork title"
              />
              <FormErrorText error={errors.artwork_name} message="Please enter the Artwork title."></FormErrorText>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <Form.Group controlId="medium">
              <Form.Label>* Artwork medium</Form.Label>
              <Form.Select {...register("medium", { required: true })} aria-label="Default select example">
                <option value="" className="placeholder">
                  Please select
                </option>
                <option value={ArtworkMedium.PAINTING}>Painting</option>
                <option value={ArtworkMedium.CERAMIC}>Ceramic</option>
                <option value={ArtworkMedium.ART_JEWELLERY}>Art Jewellery</option>
                <option value={ArtworkMedium.MULTIMEDIA_ARTWORK}>Multimedia Artwork</option>
                <option value={ArtworkMedium.SCULPTURE}>Sculpture</option>
                <option value={ArtworkMedium.ARTISTS_BOOK}>Artists’ Book</option>
                <option value={ArtworkMedium.DIGITAL_ARTWORK}>Digital Artwork</option>
                <option value={ArtworkMedium.GLASSWARE}>Glassware</option>
                <option value={ArtworkMedium.PHOTOGRAPH}>Photograph</option>
                <option value={ArtworkMedium.TAPESTRY}>Tapestry</option>
                <option value={ArtworkMedium.BATIK}>Batik</option>
                <option value={ArtworkMedium.DRAWING}>Drawing</option>
                <option value={ArtworkMedium.INSTALLATION}>Installation</option>
                <option value={ArtworkMedium.PICTURE}>Picture</option>
                <option value={ArtworkMedium.VIDEO_ARTWORK}>Video Artwork</option>
                <option value={ArtworkMedium.CARVING}>Carving</option>
                <option value={ArtworkMedium.ENGRAVING}>Engraving</option>
                <option value={ArtworkMedium.LITHOGRAPH}>Lithograph</option>
                <option value={ArtworkMedium.PRINT}>Print</option>
                <option value={ArtworkMedium.OTHER}>Other</option>
              </Form.Select>
              <FormErrorText error={errors.medium} message="Please enter an option from above."></FormErrorText>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="details">
              <Form.Label>Details or notes</Form.Label>
              <Form.Control
                {...register("description", {
                  maxLength: 255,
                })}
                type="text"
                placeholder="Please add notes on medium"
              />
              <FormErrorText
                error={errors.description}
                message="Details should be less than 256 characters length."
              ></FormErrorText>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <Form.Group controlId="height">
              <Form.Label>Height</Form.Label>
              <Form.Control
                {...register("height", {
                  validate: (value) =>
                    isUndefined(value) || isValidDimension(value)
                      ? true
                      : "Height should be a number with two decimal points",
                  setValueAs: (value) => setDimension(value as string),
                })}
                type="text"
                placeholder="Enter height (cm)"
              />
              <FormErrorText error={errors.height}></FormErrorText>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="width">
              <Form.Label>Width</Form.Label>
              <Form.Control
                {...register("width", {
                  validate: (value) =>
                    isUndefined(value) || isValidDimension(value)
                      ? true
                      : "Width should be a number with two decimal points",

                  setValueAs: (value) => setDimension(value as string),
                })}
                type="text"
                placeholder="Enter width (cm)"
              />
              <FormErrorText error={errors.width}></FormErrorText>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <Form.Group controlId="artistWorkId">
              <Form.Label>Artist work ID</Form.Label>
              <Form.Control
                {...register("artist_work_id", {
                  maxLength: 20,
                })}
                type="text"
                placeholder="Enter artist work ID"
              />
              <FormErrorText
                error={errors.artist_work_id}
                message="Artist work id should be less than 21 characters length."
              ></FormErrorText>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="galleryWorkId">
              <Form.Label>Gallery work ID</Form.Label>
              <Form.Control
                {...register("gallery_work_id", {
                  maxLength: 20,
                })}
                type="text"
                placeholder="Enter gallery work ID"
              />
              <FormErrorText
                error={errors.gallery_work_id}
                message="Artist work id should be less than 21 characters length."
              ></FormErrorText>
            </Form.Group>
          </Col>
        </Row>
        <FormNavigationButtons
          isInsertMode={isInsertMode}
          onSubmit={onSubmit}
          onCancel={handleCancel}
          handleSubmit={handleSubmit}
          submitRef={submitRef}
        />
      </Container>
    </Form>
  );
};
